import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useCompoundResourceApi } from "../../api/ResourceAPI";
import { LegalCaseItem } from "../docs/LegalCase";
import { useParams } from "react-router-dom";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import { NewsItem } from "../docs/News";
import { DiligenceItem } from "../docs/Diligence";
import { PersonInfoApi } from "../../api/Person";
import { useFamaFlags } from "../../api/FamaFlags";
import { useAppContext } from "../../AppProviders";
import { API_URL } from "../../api/helpers";

import { classNames } from "../../utils";
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  BuildingLibraryIcon,
  DocumentMagnifyingGlassIcon,
  NewspaperIcon,
} from "@heroicons/react/24/outline";
import {
  Summary360DiligenceDocument,
  Summary360LegalDocument,
  Summary360NewsDocument,
} from "./Summary360ContentDocument";
import { mergeDocuments, MergedDocument } from "./summaryDocumentOrder";

type Props = {
  personEx?: PersonInfoApi;
};

type DocumentType = "legal_case" | "news" | "diligence";

function getDocumentLabel(document: MergedDocument): string {
  if (document.type === "diligence") {
    return document.name;
  }
  return document.title;
}

const Summary360Documents = (props: Props) => {
  const { personEx } = props;
  const params = useParams();
  const report_uuid = params.id as string;
  const { v3DiligenceEnabled } = useFamaFlags();
  const { requestApi } = useAppContext();

  const {
    data: legalCases,
    isLoading: legalCasesLoading,
    error: legalCasesError,
  } = useCompoundResourceApi<LegalCaseItem>(
    `itemdal/reports/${report_uuid}/legal_cases`,
    `itemdal/legal_cases`
  );

  const {
    data: newsItems,
    isLoading: newsItemsLoading,
    error: newsItemsError,
  } = useCompoundResourceApi<NewsItem>(
    `itemdal/reports/${report_uuid}/news`,
    `itemdal/news`
  );

  const diligencePath = `itemdal/reports/${report_uuid}/diligence`;
  const {
    data: diligenceItems,
    isLoading: diligenceLoading,
    error: diligenceError,
  } = useQuery<DiligenceItem[]>({
    queryKey: [diligencePath],
    queryFn: () =>
      requestApi.get(`${API_URL}/${diligencePath}`).then((res) => res.data),
    enabled: v3DiligenceEnabled && !!report_uuid,
  });

  const [activeDocument, setActiveDocument] = useState<
    | { type: DocumentType; value: LegalCaseItem | NewsItem | DiligenceItem }
    | undefined
  >(undefined);
  const documents = useMemo(() => {
    return mergeDocuments(
      legalCases ?? [],
      newsItems ?? [],
      v3DiligenceEnabled ? diligenceItems ?? [] : []
    );
  }, [legalCases, newsItems, diligenceItems, v3DiligenceEnabled]);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  useEffect(() => {
    if (documents.length > 0) {
      setActiveDocument({
        type: documents[0].type,
        value: documents[0],
      });
    }
  }, [documents]);

  const isDiligenceFetchActive = v3DiligenceEnabled;

  if (
    legalCasesLoading ||
    newsItemsLoading ||
    (isDiligenceFetchActive && diligenceLoading)
  ) {
    return <LoadingSpinner />;
  }

  if (
    legalCasesError ||
    newsItemsError ||
    (isDiligenceFetchActive && diligenceError)
  ) {
    return (
      <div className="bg-red-300 p-4 rounded-md">
        Error loading documents. Please try again later.
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="text-gray-500">
        This report contains no additional documents.
      </div>
    );
  }

  if (!activeDocument) {
    return <LoadingSpinner />;
  }

  return (
    <div
      className={classNames(
        !isFullScreen ? "h-full relative" : "",
        isFullScreen
          ? "bg-white px-10 py-14 absolute top-0 left-0 w-full h-full overflow-y-auto"
          : "",
        "text-gray-500 flex gap-6"
      )}
    >
      <div className="absolute top-1 right-1">
        {!isFullScreen && (
          <div
            className="flex items-center gap-2 border-2 rounded-md text-green-500 border-green-600 py-2 px-4 cursor-pointer bg-white print:hidden"
            onClick={toggleFullScreen}
          >
            <ArrowsPointingOutIcon className="w-7 h-7" />
            <span className="text-sm">Open Full View</span>
          </div>
        )}
        {isFullScreen && (
          <div
            className="flex items-center gap-2 border-2 rounded-md text-green-500 border-green-600 py-2 px-4 cursor-pointer bg-white"
            onClick={toggleFullScreen}
          >
            <ArrowsPointingInIcon className="w-7 h-7" />
            <span className="text-sm">Close Full View</span>
          </div>
        )}
      </div>
      <div
        className={classNames(
          !isFullScreen ? "h-full top-0 sticky  print:hidden" : "",
          isFullScreen ? "sticky top-0 h-full overflow-y-auto" : "",
          "w-96 pr-3 "
        )}
      >
        <div className="sticky top-4">
          <div className="uppercase text-xl font-bold bg-white text-gray-800 sticky top-0 z-10">
            Table of Contents
          </div>
          <div className="flex flex-col gap-4 mt-6 relative">
            <div
              className={classNames(
                "absolute left-0 top-0 w-2 h-full rounded-md",
                "bg-gray-200"
              )}
            />
            {documents.map((document) => (
              <div
                key={document.external_id}
                className={classNames("relative pl-8")}
              >
                <div
                  className={classNames(
                    "absolute left-0 top-0 w-2 h-full rounded-md",
                    activeDocument?.value.external_id === document.external_id
                      ? "bg-gray-500"
                      : ""
                  )}
                />
                <div
                  className={classNames(
                    "flex items-start rounded-md p-2 gap-3 cursor-pointer",
                    activeDocument?.value.external_id === document.external_id
                      ? "bg-secondaryDarkBlue text-white"
                      : "bg-gray-50 text-gray-700 font-thin"
                  )}
                  onClick={() =>
                    setActiveDocument({
                      type: document.type,
                      value: document,
                    })
                  }
                >
                  <div className="w-6">
                    {document.type === "legal_case" ? (
                      <BuildingLibraryIcon className="w-6 h-6" />
                    ) : document.type === "news" ? (
                      <NewspaperIcon className="w-6 h-6" />
                    ) : (
                      <DocumentMagnifyingGlassIcon className="w-6 h-6" />
                    )}
                  </div>
                  <div className="">{getDocumentLabel(document)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={classNames("flex-1 h-full overflow-y-auto")}>
        {activeDocument.type === "legal_case" ? (
          <Summary360LegalDocument
            {...(activeDocument.value as LegalCaseItem)}
            personEx={personEx}
          />
        ) : activeDocument.type === "news" ? (
          <Summary360NewsDocument
            {...(activeDocument.value as NewsItem)}
            personEx={personEx}
          />
        ) : (
          <Summary360DiligenceDocument
            {...(activeDocument.value as DiligenceItem)}
            personEx={personEx}
          />
        )}
      </div>
    </div>
  );
};

export default Summary360Documents;
